// Page de suivi — /tracking-settings. The public page where the tenant's
// customers follow their parcels: identity, one brand colour (derived
// palette, advanced colours on demand), the header and the installation
// script. The preview is the page itself (TrackingPreview mirrors
// trackingStatic/tracking_page.js), styled by the tenant's settings.
// API: GET/PUT/DELETE /api/tracking_settings/settings (?brand_id),
// POST /api/tracking_settings/upload?kind=logo|banner, GET /api/brands/list.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Code, Contact, ImageIcon, Palette, PanelTop, RotateCcw } from 'lucide-react';
import TrackingPreview from './TrackingPreview';
import {
  AttentionCard, BrandColorPicker, BrandPicker, ColorField, DiscardDialog, EmbedCode, ImageDrop, PageSkeleton,
  PreviewCard, SaveBar, SwitchRow, authHeaders, contrastRatio, copyText, formatDay, formatRatio, kitLabels, scrollToId,
} from './ChannelsKit';
import {
  Banner, Card, ConfirmDialog, Field, OverflowMenu, PageHeader, Toast,
} from '../design/ui';
import './TrackingSettings.css';

const DEFAULT_FORM = {
  colors: {
    primary_color: '#2d2d2d',
    secondary_color: '#f8f9fa',
    background_color: '#ffffff',
    text_color: '#333333',
    border_color: '#e0e0e0',
    success_color: '#28a745',
    warning_color: '#ffc107',
    error_color: '#dc3545',
  },
  banner: {
    enabled: true,
    title: '',
    subtitle: '',
    background_image: '',
    background_image_key: null,
    background_color: '#f8f9fa',
    text_color: '#333333',
    height: 200,
  },
  branding: {
    company_name: '',
    company_logo: '',
    logo_key: null,
    show_powered_by: true,
  },
};

// The backend's default header title (TrackingBanner.title): not a text
// anyone typed.
const DEFAULT_HERO_TITLE = 'Track Your Package';

const mergeForm = (data) => ({
  colors: { ...DEFAULT_FORM.colors, ...(data.colors || {}) },
  banner: { ...DEFAULT_FORM.banner, ...(data.banner || {}) },
  branding: { ...DEFAULT_FORM.branding, ...(data.branding || {}) },
});

// « À vérifier » of the page being edited.
export const trackingChecks = (form) => {
  const checks = [];
  const { banner, branding } = form;
  const customHeader = (banner.title && banner.title !== DEFAULT_HERO_TITLE) || banner.subtitle || banner.background_image;
  if (!banner.enabled && customHeader) checks.push({ key: 'headerOff', tone: 'warning' });
  if (!String(branding.company_name || '').trim() && !branding.company_logo) checks.push({ key: 'noIdentity', tone: 'warning' });
  const ratio = contrastRatio(form.colors.primary_color);
  if (ratio !== null && ratio < 4.5) checks.push({ key: 'contrast', tone: 'warning', ratio });
  return checks;
};

// The image fields are URLs for the API (HttpUrl): an empty string is
// refused (422), « no image » is null.
export const trackingPayload = (form) => ({
  colors: form.colors,
  banner: { ...form.banner, background_image: form.banner.background_image || null },
  branding: { ...form.branding, company_logo: form.branding.company_logo || null },
});

const TrackingSettings = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'fr';
  const navigate = useNavigate();
  const kit = kitLabels(t);

  const [user, setUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [baseline, setBaseline] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewMode, setPreviewMode] = useState('search');
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [pendingBrand, setPendingBrand] = useState(null);

  const dirty = JSON.stringify(formData) !== JSON.stringify(baseline);
  const canManage = Boolean(user && ['admin', 'seller', 'eshop'].includes(user.role));

  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const loadSettings = useCallback(async (brandId) => {
    const params = brandId ? { brand_id: brandId } : {};
    const response = await axios.get('/api/tracking_settings/settings', { headers: authHeaders(), params });
    const form = mergeForm(response.data);
    setSettings(response.data);
    setFormData(form);
    setBaseline(form);
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    (async () => {
      let userData = null;
      try {
        const response = await axios.get('/api/users/user', { headers: authHeaders() });
        userData = response.data;
        setUser(userData);
      } catch (err) {
        navigate('/');
        return;
      }
      if (userData?.has_brand) {
        try {
          const response = await axios.get('/api/brands/list', { headers: authHeaders() });
          setBrands(response.data || []);
        } catch (err) {
          setBrands([]);
        }
      }
      try {
        await loadSettings('');
      } catch (err) {
        setLoadError(t('trackingSettingsV2.loadError', 'Impossible de charger les paramètres de suivi.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, loadSettings, t]);

  const selectBrand = async (brandId) => {
    setSelectedBrandId(brandId);
    setSwitching(true);
    setLoadError('');
    try {
      await loadSettings(brandId);
    } catch (err) {
      setLoadError(t('trackingSettingsV2.loadError', 'Impossible de charger les paramètres de suivi.'));
    } finally {
      setSwitching(false);
    }
  };

  const requestBrand = (entry) => {
    if (entry.key === selectedBrandId) return;
    if (dirty) setPendingBrand(entry.key);
    else selectBrand(entry.key);
  };

  const setSection = (section, patch) => setFormData((prev) => ({ ...prev, [section]: { ...prev[section], ...patch } }));

  const applyPreset = (color) => setSection('colors', {
    primary_color: color,
    secondary_color: '#F6F5F2',
    background_color: '#ffffff',
    text_color: '#1F2937',
    border_color: '#E2DCD2',
  });

  const handleImageFile = async (kind, file) => {
    if (!file) return;
    const setUploading = kind === 'logo' ? setLogoUploading : setBannerUploading;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await axios.post(`/api/tracking_settings/upload?kind=${kind}`, form, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      if (kind === 'logo') setSection('branding', { logo_key: response.data.key, company_logo: response.data.url });
      else setSection('banner', { background_image_key: response.data.key, background_image: response.data.url });
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('trackingSettingsV2.uploadError', 'Envoi impossible (PNG, JPEG ou WebP uniquement).') });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (kind) => {
    if (kind === 'logo') setSection('branding', { logo_key: null, company_logo: null });
    else setSection('banner', { background_image_key: null, background_image: null });
  };

  const handleSave = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      await axios.put('/api/tracking_settings/settings', trackingPayload(formData), { headers: authHeaders(), params });
      await loadSettings(selectedBrandId);
      setToast({ tone: 'success', text: t('trackingSettingsV2.saved', 'Paramètres de suivi enregistrés.') });
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('trackingSettingsV2.saveError', 'Enregistrement impossible. Réessayez.') });
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    setDeleteBusy(true);
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      await axios.delete('/api/tracking_settings/settings', { headers: authHeaders(), params });
      await loadSettings(selectedBrandId);
      setToast({ tone: 'success', text: t('settingsPolishUi.tracking.resetDone', 'Apparence réinitialisée.') });
      setDeleting(false);
    } catch (err) {
      setToast({ tone: 'danger', text: err.response?.data?.detail || t('settingsPolishUi.tracking.resetError', 'Réinitialisation impossible. Réessayez.') });
    } finally {
      setDeleteBusy(false);
    }
  };

  const tenantId = user ? (user.role === 'employee' ? user.admin_id : user._id) : '';
  const embedCode = `<script src="https://unanimity.ai/api/trackingStatic/tracking_page.js" data-user-id="${tenantId || ''}"${selectedBrandId ? ` data-brand-id="${selectedBrandId}"` : ''}></script>`;

  const copyEmbed = async () => {
    const ok = await copyText(embedCode);
    setToast(ok ? { tone: 'success', text: kit.embed.copiedToast } : { tone: 'danger', text: kit.embed.copyFailed });
  };

  const getBrandName = (brandId) => {
    if (!brandId) return t('trackingSettingsV2.defaultSettings', 'Paramètres par défaut');
    const brand = brands.find((b) => b.id === brandId);
    return brand ? brand.name : t('trackingSettingsV2.unknownBrand', 'Marque inconnue');
  };

  const brandEntries = user?.has_brand && brands.length > 0
    ? [{ key: '', name: getBrandName('') }, ...brands.map((brand) => ({ key: brand.id, name: brand.name }))]
      .map((entry) => ({ ...entry, swatch: false }))
    : [];

  const checks = trackingChecks(formData);
  const checkText = (check) => {
    switch (check.key) {
    case 'headerOff':
      return {
        title: t('channelsUi.tracking.checkHeaderOff', 'Votre en-tête n’est pas affiché : titre, sous-titre et image sont ignorés'),
        detail: t('channelsUi.tracking.checkHeaderOffDetail', 'Activez « Afficher votre en-tête » pour que vos clients les voient.'),
        action: { label: t('channelsUi.tracking.openHeader', 'Voir l’en-tête'), onClick: () => scrollToId('tracking-header') },
      };
    case 'noIdentity':
      return {
        title: t('channelsUi.tracking.checkNoIdentity', 'Ni logo ni nom d’entreprise : la page ne dit pas qui vous êtes'),
        detail: t('channelsUi.tracking.checkNoIdentityDetail', 'Vos clients voient une page anonyme ; ajoutez au moins le nom de l’entreprise.'),
        action: { label: t('channelsUi.tracking.openIdentity', 'Voir l’identité'), onClick: () => scrollToId('tracking-identity') },
      };
    default:
      return {
        title: t('channelsUi.checkContrast', 'Contraste faible ({{ratio}}:1) : le texte sur votre couleur est peu lisible', { ratio: formatRatio(check.ratio, lang) }),
        detail: t('channelsUi.checkContrastDetail', 'Choisissez une couleur plus foncée ou plus claire (4,5:1 au moins).'),
        action: { label: t('channelsUi.openColors', 'Voir les couleurs'), onClick: () => scrollToId('tracking-theme') },
      };
    }
  };

  const header = (
    <PageHeader
      title={t('trackingSettingsV2.title', 'Page de suivi')}
      subtitle={[
        t('channelsUi.tracking.subtitle', 'La page où vos clients suivent leurs colis, à vos couleurs.'),
        settings?.updated_at && t('channelsUi.tracking.updated', 'Modifiée le {{date}}', { date: formatDay(settings.updated_at, lang) }),
      ].filter(Boolean).join(' · ')}
      actions={(
        <>
          {canManage && (
            <OverflowMenu
              label={t('trackingSettingsV2.moreActions', 'Plus d’actions')}
              items={[{ label: t('settingsPolishUi.tracking.reset', 'Réinitialiser l’apparence'), icon: RotateCcw, danger: true, onClick: () => setDeleting(true) }]}
            />
          )}
          <button type="button" className="u-btn u-btn--secondary" onClick={copyEmbed} disabled={!tenantId}>
            <Code aria-hidden="true" />
            {kit.embed.copyCode}
          </button>
        </>
      )}
    />
  );

  if (loading) {
    return (
      <div className="u-page u-page--narrow chan-v2" aria-busy="true" aria-label={t('trackingSettingsV2.loading', 'Chargement des paramètres de suivi…')}>
        <PageSkeleton header={header} />
      </div>
    );
  }

  const { colors, banner, branding } = formData;

  return (
    <div className="u-page u-page--narrow chan-v2">
      {header}

      <div className="u-well">
        {brandEntries.length > 0 && (
          <BrandPicker label={kit.brands} entries={brandEntries} selectedKey={selectedBrandId} onSelect={requestBrand} />
        )}

        {!canManage && (
          <Banner tone="info">{t('channelsUi.tracking.adminOnly', 'Seul un administrateur du compte peut modifier la page de suivi.')}</Banner>
        )}

        {loadError ? (
          <Banner tone="danger">{loadError}</Banner>
        ) : switching ? (
          <div className="chan-v2__layout">
            <span className="u-skeleton chan-v2__card-skeleton" />
            <span className="u-skeleton chan-v2__card-skeleton" />
          </div>
        ) : (
          <>
            <AttentionCard
              items={checks.map((check) => ({ ...check, ...checkText(check) }))}
              title={kit.attention}
              criticalLabel={kit.critical}
              watchLabel={kit.watch}
            />

            <div className="chan-v2__layout">
              <div className="chan-v2__column">
                <Card
                  id="tracking-identity"
                  icon={Contact}
                  title={t('trackingSettingsV2.sectionIdentity', 'Identité')}
                  description={t('channelsUi.tracking.identityDesc', 'Le logo et le nom affichés en haut de la page.')}
                >
                  <div className="u-form">
                    <ImageDrop
                      label={t('trackingSettingsV2.logo', 'Logo')}
                      imageUrl={branding.company_logo || null}
                      fallback={(branding.company_name || '?').charAt(0).toUpperCase()}
                      hint={t('trackingSettingsV2.logoHint', 'Glissez un logo (PNG, JPEG, WebP) — affiché dans l’en-tête de la page')}
                      uploading={logoUploading}
                      uploadingLabel={t('trackingSettingsV2.uploading', 'Envoi…')}
                      onFile={(file) => handleImageFile('logo', file)}
                      onRemove={() => removeImage('logo')}
                      removeLabel={t('trackingSettingsV2.logoRemove', 'Retirer le logo')}
                    />
                    <Field label={t('trackingSettingsV2.companyName', 'Nom de l’entreprise')} htmlFor="tracking_company_name">
                      <input
                        id="tracking_company_name"
                        className="u-input"
                        value={branding.company_name || ''}
                        onChange={(e) => setSection('branding', { company_name: e.target.value })}
                      />
                    </Field>
                  </div>
                </Card>

                <Card
                  id="tracking-theme"
                  icon={Palette}
                  title={t('trackingSettingsV2.sectionTheme', 'Thème')}
                  description={t('channelsUi.tracking.themeDesc', 'Une couleur de marque : l’en-tête, les boutons et la progression du colis en dérivent.')}
                >
                  <div className="u-form">
                    <BrandColorPicker
                      value={colors.primary_color}
                      lang={lang}
                      onPick={applyPreset}
                      labels={{
                        title: t('trackingSettingsV2.brandColor', 'Couleur de marque'),
                        custom: t('trackingSettingsV2.customColor', 'Couleur personnalisée'),
                        ok: (ratio) => t('trackingSettingsV2.contrastOk', 'Contraste AA vérifié ({{ratio}}:1)', { ratio }),
                        warn: (ratio) => t('trackingSettingsV2.contrastWarn', 'Contraste faible ({{ratio}}:1) — le texte sera peu lisible', { ratio }),
                      }}
                    />
                    <details className="u-disclosure chan-v2__disclosure">
                      <summary>
                        <ChevronRight aria-hidden="true" />
                        {t('trackingSettingsV2.advancedColors', 'Réglages avancés — couleurs séparées')}
                      </summary>
                      <div className="chan-v2__disclosure-body u-form-grid">
                        {[
                          ['primary_color', t('trackingSettingsV2.primaryColor', 'Couleur principale')],
                          ['secondary_color', t('trackingSettingsV2.secondaryColor', 'Couleur secondaire')],
                          ['background_color', t('trackingSettingsV2.backgroundColor', 'Fond des cartes')],
                          ['text_color', t('trackingSettingsV2.textColor', 'Texte')],
                          ['border_color', t('trackingSettingsV2.borderColor', 'Bordures')],
                          ['success_color', t('trackingSettingsV2.successColor', 'Succès')],
                          ['warning_color', t('trackingSettingsV2.warningColor', 'Avertissement')],
                          ['error_color', t('trackingSettingsV2.errorColor', 'Erreur')],
                        ].map(([name, label]) => (
                          <ColorField key={name} id={`tracking_${name}`} label={label} value={colors[name]} onChange={(v) => setSection('colors', { [name]: v })} />
                        ))}
                      </div>
                    </details>
                  </div>
                </Card>

                <Card
                  id="tracking-header"
                  icon={PanelTop}
                  title={t('trackingSettingsV2.sectionWelcome', 'Accueil')}
                  description={t('channelsUi.tracking.headerDesc', 'Le haut de la page : votre titre, une phrase et une image si vous le souhaitez.')}
                >
                  <div className="u-form">
                    <div className="chan-v2__switches">
                      <SwitchRow
                        id="tracking_banner_enabled"
                        label={t('channelsUi.tracking.headerEnabled', 'Afficher votre en-tête')}
                        help={t('channelsUi.tracking.headerEnabledHelp', 'Désactivé : la page affiche « Suivez votre colis » sur votre couleur, sans sous-titre ni image.')}
                        checked={banner.enabled}
                        onChange={(value) => setSection('banner', { enabled: value })}
                      />
                    </div>
                    <div className="u-form-grid">
                      <Field label={t('trackingSettingsV2.heroTitle', 'Titre')} htmlFor="tracking_banner_title">
                        <input
                          id="tracking_banner_title"
                          className="u-input"
                          value={banner.title || ''}
                          maxLength={80}
                          placeholder={t('trackingSettingsV2.heroTitlePlaceholder', 'Suivez votre colis')}
                          onChange={(e) => setSection('banner', { title: e.target.value })}
                        />
                      </Field>
                      <Field label={t('trackingSettingsV2.heroSubtitle', 'Sous-titre')} htmlFor="tracking_banner_subtitle">
                        <input
                          id="tracking_banner_subtitle"
                          className="u-input"
                          value={banner.subtitle || ''}
                          maxLength={140}
                          placeholder={t('trackingSettingsV2.heroSubtitlePlaceholder', 'Entrez votre e-mail pour suivre vos commandes en temps réel.')}
                          onChange={(e) => setSection('banner', { subtitle: e.target.value })}
                        />
                      </Field>
                    </div>
                    <ImageDrop
                      wide
                      label={t('trackingSettingsV2.heroImage', 'Image d’en-tête (optionnelle)')}
                      imageUrl={banner.background_image || null}
                      fallback={<ImageIcon strokeWidth={1.8} aria-hidden="true" />}
                      hint={t('trackingSettingsV2.heroImageHint', 'Glissez une image — assombrie automatiquement pour garder le titre lisible')}
                      uploading={bannerUploading}
                      uploadingLabel={t('trackingSettingsV2.uploading', 'Envoi…')}
                      onFile={(file) => handleImageFile('banner', file)}
                      onRemove={() => removeImage('banner')}
                      removeLabel={t('trackingSettingsV2.heroImageRemove', 'Retirer l’image')}
                    />
                  </div>
                </Card>

                <Card
                  id="tracking-install"
                  icon={Code}
                  title={t('channelsUi.install', 'Installation')}
                  description={t('trackingSettingsV2.embedHint', 'Collez ce script sur la page de votre site où le suivi doit apparaître.')}
                >
                  <div className="u-form">
                    <div className="u-field">
                      <span className="u-label">{t('trackingSettingsV2.embedCode', 'Code d’intégration')}</span>
                      <EmbedCode
                        code={embedCode}
                        copyLabel={t('trackingSettingsV2.copy', 'Copier')}
                        copiedLabel={t('trackingSettingsV2.copied', 'Copié')}
                        loadingLabel={kit.embed.loading}
                      />
                    </div>
                    <div className="chan-v2__switches">
                      <SwitchRow
                        id="tracking_show_powered_by"
                        label={t('trackingSettingsV2.showPoweredBy', 'Afficher « Propulsé par Unanimity AI »')}
                        help={t('channelsUi.poweredByHelp', 'La mention discrète en bas du widget.')}
                        checked={branding.show_powered_by}
                        onChange={(value) => setSection('branding', { show_powered_by: value })}
                      />
                    </div>
                  </div>
                </Card>

                {canManage && (
                  <SaveBar
                    dirty={dirty}
                    saving={saving}
                    canSave={dirty}
                    onSave={handleSave}
                    onReset={() => setFormData(baseline)}
                    note={brandEntries.length > 0 ? getBrandName(selectedBrandId) : null}
                    labels={kit.save}
                  />
                )}
              </div>

              <PreviewCard
                title={kit.preview}
                modes={[
                  { value: 'search', label: t('trackingSettingsV2.modeSearch', 'Recherche') },
                  { value: 'result', label: t('trackingSettingsV2.modeResult', 'Résultat') },
                  { value: 'desktop', label: t('trackingSettingsV2.modeDesktop', 'Bureau') },
                ]}
                mode={previewMode}
                onMode={setPreviewMode}
                modesLabel={t('trackingSettingsV2.previewModes', 'Aperçu')}
                note={t('channelsUi.tracking.previewNote', 'La page telle que vos clients la voient, avec un colis d’exemple.')}
              >
                <TrackingPreview settings={formData} mode={previewMode} t={t} />
              </PreviewCard>
            </div>
          </>
        )}
      </div>

      {pendingBrand !== null && (
        <DiscardDialog
          t={t}
          name={getBrandName(selectedBrandId)}
          onConfirm={() => { selectBrand(pendingBrand); setPendingBrand(null); }}
          onCancel={() => setPendingBrand(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={t('settingsPolishUi.tracking.resetTitle', 'Réinitialiser l’apparence de « {{name}} » ?', { name: getBrandName(selectedBrandId) })}
          message={t('settingsPolishUi.tracking.resetMessage', 'Les couleurs, l’en-tête (titre, sous-titre, image) et l’identité (nom de l’entreprise, logo, mention « Propulsé par ») reprennent les réglages par défaut sur la page de suivi. Le script d’installation ne change pas. Cette action est irréversible.')}
          confirmLabel={t('settingsPolishUi.tracking.reset', 'Réinitialiser l’apparence')}
          cancelLabel={t('trackingSettingsV2.cancel', 'Annuler')}
          icon={RotateCcw}
          busy={deleteBusy}
          onConfirm={runDelete}
          onCancel={() => setDeleting(false)}
        />
      )}

      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={kit.close}>{toast.text}</Toast>
      )}
    </div>
  );
};

export default TrackingSettings;
